import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";

const navItems = [
  { to: "/what-we-do", label: "What We Do" },
  { to: "/industry-reach", label: "Industry Reach" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/clients", label: "Our Clients" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/our-approach", label: "Our Approach" },
  { to: "/faq", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // The home hero is a dark cinematic video, flip nav text to light
  // when sitting transparently over it. Everywhere else, use dark text.
  const onDarkHero = location.pathname === "/" && !scrolled;
  const linkBase = onDarkHero
    ? "text-white/85 hover:text-white"
    : "text-slate-500 hover:text-slate-900";
  const linkActive = onDarkHero ? "text-white" : "text-slate-900";
  const logoFilter = onDarkHero ? "brightness-0 invert" : "";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : onDarkHero
          ? "bg-zinc-950/60 backdrop-blur-md border-b border-white/10"
          : "bg-white/85 backdrop-blur-xl border-b border-slate-200"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center group"
        >
          <img
            src="/highways-industry-logo.png"
            alt="HighwaysIndustry.com"
            className={`h-10 md:h-14 w-auto ${logoFilter}`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? linkActive : linkBase
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/positioning-review"
            data-testid="cta-define-position-nav"
            className="hidden md:inline-flex items-center gap-2 bg-primary-blue text-white px-5 py-2.5 text-sm font-semibold hover:glow-blue transition-all rounded-full"
          >
            Book A Discovery Call
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className={`xl:hidden p-3 border transition-colors rounded-full ${
              onDarkHero
                ? "border-white/30 text-white hover:border-accent-blue"
                : "border-slate-300 text-slate-900 hover:border-accent-blue"
            }`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200"
        >
          <div className="px-5 py-6 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-3 text-base font-medium border-b border-slate-100 ${
                    isActive ? "text-accent-blue" : "text-slate-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/positioning-review"
              data-testid="cta-define-position-mobile"
              className="mt-4 block text-center bg-primary-blue text-white px-4 py-4 text-sm font-semibold rounded-full"
            >
              Book A Discovery Call →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
